import React, { useState, useEffect } from 'react';

const Configuration = ({ selectedMedication, medicationDetails, onConfigurationChange }) => {
    const [configuration, setConfiguration] = useState({
        posologia: '1 comprimido',
        frecuencia: 'Cada 8 horas',
        viaAdministracion: 'Oral',
        duracionTratamiento: 7,
        unidadTiempo: 'Días',
        numeroEnvases: 1,
        tipoPrescripcion: 'Aguda',
        instruccionesEspeciales: '',
        renovacionAutomatica: false,
        protectorGastrico: false
    });

    const [alerts, setAlerts] = useState([]);
    const [interactions, setInteractions] = useState([]);

    useEffect(() => {
        if (selectedMedication) {
            const newConfig = { ...configuration };
            
            const formaFarmaceutica = getFormaFarmaceutica();
            newConfig.viaAdministracion = determineAdministrationRoute(formaFarmaceutica);
            newConfig.posologia = determineSuggestedDosage();
            newConfig.instruccionesEspeciales = '';
            newConfig.protectorGastrico = false;
            
            setConfiguration(newConfig);
            checkMedicationAlerts();
        }
    }, [selectedMedication, medicationDetails]);

    useEffect(() => {
        if (onConfigurationChange) {
            onConfigurationChange(configuration);
        }
    }, [configuration, onConfigurationChange]);

    const getFormaFarmaceutica = () => {
        if (medicationDetails && medicationDetails.formaFarmaceutica) {
            return medicationDetails.formaFarmaceutica;
        }
        
        if (selectedMedication && selectedMedication.nombre) {
            const nombre = selectedMedication.nombre.toLowerCase();
            if (nombre.includes('comprimido')) return 'Comprimido';
            if (nombre.includes('cápsula')) return 'Cápsula';
            if (nombre.includes('jarabe')) return 'Jarabe';
            if (nombre.includes('solución')) return 'Solución';
            if (nombre.includes('pomada')) return 'Pomada';
            if (nombre.includes('crema')) return 'Crema';
            if (nombre.includes('gel')) return 'Gel';
            if (nombre.includes('inyectable')) return 'Inyectable';
            if (nombre.includes('gotas')) return 'Gotas';
            if (nombre.includes('spray')) return 'Spray';
        }
        return 'No especificado';
    };

    const determineAdministrationRoute = (formaFarmaceutica) => {
        const forma = formaFarmaceutica.toLowerCase();
        if (forma.includes('comprimido') || forma.includes('cápsula') || forma.includes('jarabe')) {
            return 'Oral';
        }
        if (forma.includes('pomada') || forma.includes('crema') || forma.includes('gel')) {
            return 'Tópica';
        }
        if (forma.includes('inyectable') || forma.includes('ampolla')) {
            return 'Intravenosa';
        }
        if (forma.includes('gotas')) {
            return 'Oftálmica';
        }
        if (forma.includes('spray')) {
            return 'Nasal';
        }
        return 'Oral';
    };

    const determineSuggestedDosage = () => {
        if (!selectedMedication?.nombre) return '1 comprimido';
        
        const nombre = selectedMedication.nombre.toLowerCase();
        
        if (nombre.includes('paracetamol')) {
            return '1 comprimido';
        }
        if (nombre.includes('ibuprofeno')) {
            return '1 comprimido';
        }
        if (nombre.includes('omeprazol')) {
            return '1 cápsula';
        }
        if (nombre.includes('amoxicilina')) {
            return '1 cápsula';
        }
        
        const forma = getFormaFarmaceutica().toLowerCase();
        if (forma.includes('jarabe')) return '5ml';
        if (forma.includes('gotas')) return '2 gotas';
        if (forma.includes('pomada') || forma.includes('crema')) return 'Aplicar una fina capa';
        
        return '1 comprimido';
    };

    const checkMedicationAlerts = () => {
        if (!selectedMedication) return;
        
        const newAlerts = [];
        const newInteractions = [];
        
        const medicationName = selectedMedication.nombre?.toLowerCase() || '';
        
        if (medicationName.includes('ibuprofeno') || medicationName.includes('diclofenaco') || medicationName.includes('naproxeno')) {
            newAlerts.push({
                type: 'warning',
                title: 'Precaución: AINE',
                message: 'Este medicamento puede causar irritación gástrica. Considere administrar con alimentos y evaluar la necesidad de protección gástrica.'
            });
        }
        
        if (medicationName.includes('metamizol')) {
            newAlerts.push({
                type: 'danger',
                title: 'Alerta: Metamizol',
                message: 'Riesgo de agranulocitosis. Vigilar signos de infección. Contraindicado en pacientes con discrasias sanguíneas.'
            });
        }
        
        if (medicationName.includes('diazepam') || medicationName.includes('lorazepam') || medicationName.includes('alprazolam')) {
            newAlerts.push({
                type: 'warning',
                title: 'Benzodiacepina',
                message: 'Riesgo de dependencia. Evitar uso prolongado. Precaución en ancianos por riesgo de caídas.'
            });
        }
        
        if (medicationName.includes('warfarina') || medicationName.includes('acenocumarol')) {
            newAlerts.push({
                type: 'danger',
                title: 'Anticoagulante',
                message: 'Requiere monitorización del INR. Múltiples interacciones medicamentosas. Riesgo hemorrágico.'
            });
        }
        
        if (medicationName.includes('ibuprofeno')) {
            newInteractions.push({
                medication: 'Omeprazol',
                interaction: 'El paciente puede tener prescrito un IBP. El ibuprofeno puede reducir la eficacia de algunos antiácidos.',
                severity: 'moderate',
                recommendation: 'Considerar administrar con protector gástrico.'
            });
        }
        
        setAlerts(newAlerts);
        setInteractions(newInteractions);
    };

    const handleInputChange = (field, value) => {
        setConfiguration(prev => ({
            ...prev,
            [field]: value
        }));
        
        if (field === 'duracionTratamiento' && value > 10) {
            const medicationName = selectedMedication?.nombre?.toLowerCase() || '';
            if (medicationName.includes('ibuprofeno') || medicationName.includes('diclofenaco')) {
                setAlerts(prev => [
                    ...prev.filter(alert => !alert.title.includes('Duración prolongada')),
                    {
                        type: 'warning',
                        title: 'Duración prolongada de AINE',
                        message: 'Tratamientos prolongados con AINEs aumentan el riesgo de efectos gastrointestinales y cardiovasculares.'
                    }
                ]);
            }
        }
    };

    const getMedicationName = () => {
        return selectedMedication?.nombre || 'Ningún medicamento seleccionado';
    };

    const getPrincipioActivo = () => {
        return selectedMedication?.pactivos || 
               medicationDetails?.pactivos || 
               'No especificado';
    };

    const getLaboratory = () => {
        return selectedMedication?.labtitular || 
               selectedMedication?.laboratorio || 
               'No especificado';
    };

    const getRegistrationNumber = () => {
        return selectedMedication?.nregistro || 
               selectedMedication?.numeroRegistro || 
               'N/A';
    };

    if (!selectedMedication) {
        return (
            <div className="card shadow-sm mb-4">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0 d-flex align-items-center">
                        <span className="badge bg-white text-primary rounded-circle me-3" style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                            3
                        </span>
                        Configuración de la prescripción
                    </h5>
                </div>
                <div className="card-body text-center py-5">
                    <div className="mb-3" style={{ fontSize: '48px' }}>💊</div>
                    <h3 className="text-secondary mb-2">Selecciona un medicamento</h3>
                    <p className="text-muted">
                        Para continuar con la configuración de la prescripción, primero debes seleccionar un medicamento en el paso anterior.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="card shadow-sm mb-4">
            <div className="card-header bg-primary text-white">
                <h5 className="mb-0 d-flex align-items-center">
                    <span className="badge bg-white text-primary rounded-circle me-3" style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                        3
                    </span>
                    Configuración de la prescripción
                </h5>
            </div>

            <div className="card-body">
                {/* Información del medicamento */}
                <div className="alert alert-info border-info mb-4">
                    <h6 className="alert-heading text-primary mb-2">
                        Medicamento seleccionado: {getMedicationName()}
                    </h6>
                    <small className="d-block text-dark">
                        <strong>Principio activo:</strong> {getPrincipioActivo()}
                        {getLaboratory() !== 'No especificado' && (
                            <> • <strong>Laboratorio:</strong> {getLaboratory()}</>
                        )}
                        <> • <strong>Forma farmacéutica:</strong> {getFormaFarmaceutica()}</>
                        <> • <strong>CN:</strong> {getRegistrationNumber()}</>
                    </small>
                </div>

                {/* Alertas */}
                {alerts.length > 0 && (
                    <div className="mb-4">
                        {alerts.map((alert, index) => (
                            <div key={index} className={`alert alert-${alert.type === 'danger' ? 'danger' : 'warning'} d-flex align-items-start mb-3`}>
                                <div className="me-3" style={{ fontSize: '1.5rem' }}>⚠️</div>
                                <div className="flex-grow-1">
                                    <h6 className="alert-heading mb-1">{alert.title}</h6>
                                    <small>{alert.message}</small>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Interacciones */}
                {interactions.length > 0 && (
                    <div className="mb-4">
                        <h6 className="text-info mb-3">Interacciones detectadas</h6>
                        {interactions.map((interaction, index) => (
                            <div key={index} className="alert alert-info mb-3">
                                <h6 className="alert-heading mb-1">Interacción con {interaction.medication}</h6>
                                <p className="mb-2 small">{interaction.interaction}</p>
                                <small className="fst-italic text-info">
                                    <strong>Recomendación:</strong> {interaction.recommendation}
                                </small>
                            </div>
                        ))}
                    </div>
                )}

                {/* Formulario */}
                <div className="row g-3 mb-4">
                    <div className="col-md-6">
                        <label className="form-label fw-medium">Posología</label>
                        <select 
                            className="form-select"
                            value={configuration.posologia}
                            onChange={(e) => handleInputChange('posologia', e.target.value)}
                        >
                            <option value="1 comprimido">1 comprimido</option>
                            <option value="2 comprimidos">2 comprimidos</option>
                            <option value="1/2 comprimido">1/2 comprimido</option>
                            <option value="1 cápsula">1 cápsula</option>
                            <option value="5ml">5ml</option>
                            <option value="10ml">10ml</option>
                            <option value="2 gotas">2 gotas</option>
                            <option value="Aplicar una fina capa">Aplicar una fina capa</option>
                            <option value="Otra">Otra</option>
                        </select>
                    </div>

                    <div className="col-md-6">
                        <label className="form-label fw-medium">Frecuencia</label>
                        <select 
                            className="form-select"
                            value={configuration.frecuencia}
                            onChange={(e) => handleInputChange('frecuencia', e.target.value)}
                        >
                            <option value="Cada 4 horas">Cada 4 horas</option>
                            <option value="Cada 6 horas">Cada 6 horas</option>
                            <option value="Cada 8 horas">Cada 8 horas</option>
                            <option value="Cada 12 horas">Cada 12 horas</option>
                            <option value="Cada 24 horas">Cada 24 horas</option>
                            <option value="1 vez al día">1 vez al día</option>
                            <option value="2 veces al día">2 veces al día</option>
                            <option value="3 veces al día">3 veces al día</option>
                            <option value="Según necesidad">Según necesidad</option>
                            <option value="Otra">Otra</option>
                        </select>
                    </div>

                    <div className="col-md-6">
                        <label className="form-label fw-medium">Vía de administración</label>
                        <select 
                            className="form-select"
                            value={configuration.viaAdministracion}
                            onChange={(e) => handleInputChange('viaAdministracion', e.target.value)}
                        >
                            <option value="Oral">Oral</option>
                            <option value="Tópica">Tópica</option>
                            <option value="Oftálmica">Oftálmica</option>
                            <option value="Nasal">Nasal</option>
                            <option value="Intravenosa">Intravenosa</option>
                            <option value="Intramuscular">Intramuscular</option>
                            <option value="Subcutánea">Subcutánea</option>
                            <option value="Rectal">Rectal</option>
                            <option value="Vaginal">Vaginal</option>
                            <option value="Otra">Otra</option>
                        </select>
                    </div>

                    <div className="col-md-6">
                        <label className="form-label fw-medium">Duración del tratamiento</label>
                        <div className="input-group">
                            <input 
                                type="number"
                                className="form-control"
                                value={configuration.duracionTratamiento}
                                onChange={(e) => handleInputChange('duracionTratamiento', parseInt(e.target.value))}
                                min="1"
                            />
                            <select 
                                className="form-select"
                                value={configuration.unidadTiempo}
                                onChange={(e) => handleInputChange('unidadTiempo', e.target.value)}
                                style={{ maxWidth: '120px' }}
                            >
                                <option value="Días">Días</option>
                                <option value="Semanas">Semanas</option>
                                <option value="Meses">Meses</option>
                            </select>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <label className="form-label fw-medium">Número de envases</label>
                        <input 
                            type="number"
                            className="form-control"
                            value={configuration.numeroEnvases}
                            onChange={(e) => handleInputChange('numeroEnvases', parseInt(e.target.value))}
                            min="1"
                        />
                    </div>

                    <div className="col-md-6">
                        <label className="form-label fw-medium">Tipo de prescripción</label>
                        <select 
                            className="form-select"
                            value={configuration.tipoPrescripcion}
                            onChange={(e) => handleInputChange('tipoPrescripcion', e.target.value)}
                        >
                            <option value="Aguda">Aguda</option>
                            <option value="Crónica">Crónica</option>
                        </select>
                    </div>

                    <div className="col-12">
                        <label className="form-label fw-medium">Instrucciones especiales para el paciente</label>
                        <textarea 
                            className="form-control"
                            value={configuration.instruccionesEspeciales}
                            onChange={(e) => handleInputChange('instruccionesEspeciales', e.target.value)}
                            placeholder="Escriba las instrucciones especiales para el paciente..."
                            rows="3"
                        />
                    </div>
                </div>

                {/* Checkboxes */}
                <div className="mb-3">
                    <div className="form-check mb-2">
                        <input 
                            className="form-check-input"
                            type="checkbox"
                            id="renovacionAutomatica"
                            checked={configuration.renovacionAutomatica}
                            onChange={(e) => handleInputChange('renovacionAutomatica', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="renovacionAutomatica">
                            Habilitar renovación automática (tratamiento crónico)
                        </label>
                    </div>

                    <div className="form-check">
                        <input 
                            className="form-check-input"
                            type="checkbox"
                            id="protectorGastrico"
                            checked={configuration.protectorGastrico}
                            onChange={(e) => handleInputChange('protectorGastrico', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="protectorGastrico">
                            Añadir protector gástrico
                        </label>
                    </div>
                </div>

                {/* Resumen */}
                <div className="alert alert-secondary mb-0">
                    <h6 className="text-secondary mb-2">Resumen de la prescripción</h6>
                    <small className="d-block lh-lg">
                        <strong>{getMedicationName()}</strong><br />
                        {configuration.posologia} • {configuration.frecuencia} • Vía {configuration.viaAdministracion}<br />
                        Duración: {configuration.duracionTratamiento} {configuration.unidadTiempo} • {configuration.numeroEnvases} envase(s)<br />
                        Tipo: {configuration.tipoPrescripcion}
                        {configuration.renovacionAutomatica && ' • Renovación automática habilitada'}
                        {configuration.protectorGastrico && ' • Con protector gástrico'}
                        {configuration.instruccionesEspeciales && (
                            <>
                                <br />
                                <strong>Instrucciones:</strong> {configuration.instruccionesEspeciales}
                            </>
                        )}
                    </small>
                </div>
            </div>
        </div>
    );
};

export default Configuration;